<template>
  <div class="position-relative overflow-hidden">
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <b-spinner variant="primary"></b-spinner>
    </div>

    <div :class="{'loading-blur': loading}">
      <div class="d-flex justify-content-between mb-3">
        <h4>Vehicle Utilization</h4>
        <div class="d-flex">
          <b-button-group size="sm" class="mr-3">
            <b-button
              :variant="viewMode === 'table' ? 'primary' : 'outline-primary'"
              @click="$emit('update:viewMode', 'table')"
            >
              Table
            </b-button>
            <b-button
              :variant="viewMode === 'chart' ? 'primary' : 'outline-primary'"
              @click="$emit('update:viewMode', 'chart')"
            >
              Graph
            </b-button>
          </b-button-group>
          <b-form-input
            :value="localFilter"
            placeholder="Filter..."
            size="sm"
            style="width: 150px;"
            class="mr-2"
            @input="onFilterInput"
          />
          <div v-if="viewMode === 'chart'" class="d-flex align-items-center mr-2">
            <span class="small font-weight-bold text-muted mr-1">Top:</span>
            <b-form-select
              :value="topN"
              :options="topNOptions"
              size="sm"
              style="width: 70px;"
              @change="$emit('update:topN', $event)"
            />
          </div>
          <b-button
            v-if="showExport"
            variant="outline-success"
            size="sm"
            @click="$emit('export')"
            :disabled="isExporting || !items.length"
            class="shadow-sm font-weight-bold"
          >
            <template v-if="isExporting">
              <b-spinner small class="mr-1"></b-spinner>
              Exporting…
            </template>
            <template v-else>
              <i class="fa fa-download mr-1"></i>
              Excel Export
            </template>
          </b-button>
        </div>
      </div>

      <div v-if="viewMode === 'chart'" class="mb-4 pt-4 d-flex justify-content-center">
        <apexchart type="radialBar" height="880" width="880" :options="chartOptions" :series="chartSeries" />
      </div>

      <b-table
        v-if="viewMode === 'table'"
        :items="items"
        :fields="fields"
        :filter="localFilter"
        hover
        responsive
        class="custom-table"
      >
        <template slot="regNo" slot-scope="data">
          <div class="d-flex align-items-center">
            <i 
                class="fa fa-info-circle text-info mr-2 cursor-pointer" 
                v-b-tooltip.hover title="View Technical Details"
                @click.stop="$emit('open-master-details', data.item)"
            ></i>
            <a
                href="javascript:void(0)"
                @click.stop="$emit('select-vehicle', data.item.regNo)"
                class="font-weight-bold text-primary vehicle-link"
            >
                {{ data.item.regNo }}
            </a>
          </div>
        </template>
        <template slot="trips" slot-scope="data">
          <button
            v-if="canOpenTrips(data.item)"
            type="button"
            class="btn btn-link p-0 font-weight-bold text-primary trip-assign-link"
            @click.stop="$emit('open-trip-details', data.item)"
          >
            {{ data.item.trips }}
          </button>
          <span v-else class="text-muted">{{ data.item.trips }}</span>
        </template>
        <template slot="totalAssignments" slot-scope="data">
          <button
            v-if="canOpenAssignments(data.item)"
            type="button"
            class="btn btn-link p-0 font-weight-bold text-primary trip-assign-link"
            @click.stop="$emit('open-assignment-details', data.item)"
          >
            {{ data.item.totalAssignments }}
          </button>
          <span v-else class="text-muted">{{ data.item.totalAssignments != null ? data.item.totalAssignments : 0 }}</span>
        </template>
        <template slot="performance" slot-scope="data">
          <b-progress :value="data.item.performance" variant="primary" show-progress height="10px" />
        </template>
        <template slot="lastLocation" slot-scope="data">
          <div class="small d-flex align-items-center location-click" @click.stop="$emit('select-vehicle', data.item.regNo)">
            <md-icon size="16" class="mr-1 text-primary">place</md-icon>
            <span class="location-text">{{ data.item.lastLocation }}</span>
          </div>
        </template>
        <template slot="revenue" slot-scope="data">
          <span class="font-weight-bold">{{ formatInr(data.item.revenue) }}</span>
        </template>
        <template slot="expense" slot-scope="data">
          <span>{{ formatInr(data.item.expense) }}</span>
        </template>
        <template slot="actions" slot-scope="row">
          <b-button
            size="sm"
            variant="outline-primary"
            @click.stop="$emit('open-trip-details', row.item)"
          >
            <md-icon>visibility</md-icon>
          </b-button>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
export default {
  name: "UtilizationTab",
  props: {
    items: { type: Array, required: true },
    fields: { type: Array, required: true },
    chartOptions: { type: Object, required: true },
    chartSeries: { type: Array, required: true },
    viewMode: { type: String, default: "table" },
    vehicleFilter: { type: String, default: "" },
    topN: { type: [Number, String], default: 5 },
    showExport: { type: Boolean, default: false },
    isExporting: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      localFilter: this.vehicleFilter || "",
    };
  },
  watch: {
    vehicleFilter(v) {
      if (v !== this.localFilter) this.localFilter = v || "";
    },
  },
  computed: {
    topNOptions() {
      const total = this.items.length;
      const opts = [];
      const baseValues = [3, 5, 10, 15, 20, 30, 40, 50, 60, 80, 100];

      baseValues.forEach((v) => {
        if (total > v) {
          opts.push({ text: String(v), value: v });
        }
      });

      opts.push({ text: "All", value: "all" });
      return opts;
    },
  },
  methods: {
    onFilterInput(v) {
      this.localFilter = v;
      this.$emit("update:vehicleFilter", v);
    },
    canOpenTrips(item) {
      return Number(item.trips) > 0;
    },
    canOpenAssignments(item) {
      return Number(item.totalAssignments) > 0;
    },
    formatInr(val) {
      const n = Number(val);
      if (!Number.isFinite(n)) return "—";
      return "₹" + n.toLocaleString("en-IN");
    },
  },
};
</script>

<style scoped>
.vehicle-link {
  text-decoration: none;
  border-bottom: 1px dashed transparent;
  transition: all 0.2s;
  cursor: pointer;
}
.vehicle-link:hover {
  text-decoration: none !important;
  border-bottom-color: #3b82f6;
  opacity: 0.8;
}
.custom-table {
  background: white;
}
.location-click {
  cursor: pointer;
  transition: opacity 0.2s;
}
.location-click:hover {
  opacity: 0.7;
}
.location-text:hover {
  text-decoration: underline;
}
.trip-assign-link {
  text-decoration: none;
  border-bottom: 1px dashed rgba(0, 123, 255, 0.35);
  cursor: pointer;
}
.trip-assign-link:hover {
  text-decoration: none;
  border-bottom-color: #007bff;
}
</style>



// WEBPACK FOOTER //
// UtilizationTab.vue?000a24c4